import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, FloppyDisk, Sparkle, Warning } from "@phosphor-icons/react";
import {
  getDriProfiles, getDri, getCategories, getIngredientsAll, getIngredientsTop,
  previewRecipe, saveBuilderRecipe, getKidsConfig,
  NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, VITAMIN_KEYS, MINERAL_KEYS,
} from "../lib/api";
import NutrientView from "../components/NutrientView";

const RANK_NUTRIENTS = ["protein", "fiber", "iron", "calcium", "vitamin_c", "vitamin_d", "omega3", "magnesium", "potassium", "vitamin_a", "folate", "zinc"];
const DIET_FILTERS = [
  { key: "all",        label: "All",         match: () => true },
  { key: "vegan",      label: "Vegan",       match: (i) => !["protein","dairy"].includes(i.category) || i.category === "legume" },
  { key: "vegetarian", label: "Vegetarian",  match: (i) => i.category !== "protein" || i.name === "egg" },
  { key: "gluten_free",label: "Gluten-free", match: (i) => !["bread","pasta","oats"].includes(i.name) },
  { key: "low_sodium", label: "Low-sodium",  match: (i) => (i.nutrients?.sodium ?? 0) < 100 },
];

function fmt(v, d = 1) {
  if (v == null || Number.isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(d);
}

export default function RecipeBuilder() {
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [profileKey, setProfileKey] = useState("adult_male_19_30");
  const [dri, setDri] = useState(null);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeRank, setActiveRank] = useState(null); // rank by nutrient
  const [dietFilter, setDietFilter] = useState("all");
  const [servings, setServings] = useState(1);
  const [recipeName, setRecipeName] = useState("My Custom Recipe");
  const [selected, setSelected] = useState([]); // {rid, name, quantity, unit, category, color}
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [foodEmoji, setFoodEmoji] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [p, c, kc] = await Promise.all([getDriProfiles(), getCategories(), getKidsConfig()]);
        setProfiles(p.profiles || []); setCategories(c.categories || []);
        setFoodEmoji(kc?.food_emoji || {});
      } catch (e) { toast.error("Failed to load metadata"); }
    })();
  }, []);

  useEffect(() => { (async () => {
    try { const d = await getDri(profileKey); setDri(d.values || {}); }
    catch (e) { /* ignore */ }
  })(); }, [profileKey]);

  const loadIngredients = useCallback(async () => {
    try {
      if (activeRank) {
        const d = await getIngredientsTop(activeRank, 10);
        setIngredients(d.ingredients || []);
      } else {
        const d = await getIngredientsAll(activeCategory === "all" ? null : activeCategory);
        setIngredients(d.ingredients || []);
      }
    } catch (e) { toast.error("Failed to load ingredients"); }
  }, [activeCategory, activeRank]);

  useEffect(() => { loadIngredients(); }, [loadIngredients]);

  const filtered = useMemo(() => {
    const filter = DIET_FILTERS.find(f => f.key === dietFilter) || DIET_FILTERS[0];
    return ingredients.filter(filter.match);
  }, [ingredients, dietFilter]);

  const addIngredient = (ing) => {
    setSelected(prev => [...prev, {
      rid: crypto.randomUUID(), name: ing.name, quantity: 100, unit: "g",
      category: ing.category, color: ing.color,
    }]);
  };

  const updateRow = (rid, key, val) => {
    setSelected(prev => prev.map(r => r.rid === rid ? { ...r, [key]: val } : r));
  };
  const removeRow = (rid) => setSelected(prev => prev.filter(r => r.rid !== rid));

  // Live preview computation, debounced
  useEffect(() => {
    if (selected.length === 0) { setPreview(null); return; }
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const payload = selected.filter(r => r.name && Number(r.quantity) > 0)
          .map(r => ({ name: r.name, quantity: Number(r.quantity), unit: r.unit }));
        if (!payload.length) { setPreview(null); setPreviewing(false); return; }
        const p = await previewRecipe(recipeName || "Draft", Number(servings) || 1, payload);
        setPreview(p);
      } catch (e) { /* ignore transient */ }
      finally { setPreviewing(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [selected, servings, recipeName]);

  const save = async () => {
    if (!selected.length) { toast.error("Add at least one ingredient"); return; }
    setSaving(true);
    try {
      const payload = selected.filter(r => r.name && Number(r.quantity) > 0)
        .map(r => ({ name: r.name, quantity: Number(r.quantity), unit: r.unit }));
      const rec = await saveBuilderRecipe(recipeName || "My Custom Recipe", Number(servings) || 1, payload);
      toast.success("Recipe saved");
      nav(`/recipe/${rec.id}`);
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const per = preview?.per_serving || {};

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="builder-page">
      <div className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Interactive builder</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Build a recipe</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">Pick a daily-intake profile, choose ingredients from the library, and watch your nutrient totals + deficits update live. Save when you're happy.</p>
      </div>

      {/* Profile & recipe meta */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_auto] gap-px bg-border border border-border" data-testid="builder-controls">
        <div className="bg-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Daily profile</div>
          <select value={profileKey} onChange={e => setProfileKey(e.target.value)}
            data-testid="dri-profile-select"
            className="w-full h-9 px-2 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200">
            {profiles.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        <div className="bg-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Recipe name</div>
          <input value={recipeName} onChange={e => setRecipeName(e.target.value)}
            data-testid="builder-name"
            className="w-full h-9 px-2 border border-border bg-background focus:outline-none focus:border-primary transition-colors duration-200" />
        </div>
        <div className="bg-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Servings</div>
          <input type="number" min="1" value={servings} onChange={e => setServings(e.target.value)}
            data-testid="builder-servings"
            className="w-full h-9 px-2 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
        </div>
        <div className="bg-card p-4 flex items-end">
          <button onClick={save} disabled={saving || !selected.length}
            data-testid="save-builder-recipe"
            className="w-full h-9 px-4 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors duration-200">
            <FloppyDisk size={14} /> Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
        {/* Ingredient library */}
        <div className="border border-border" data-testid="ingredient-library">
          <div className="px-4 py-3 border-b border-border">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Ingredient library</div>
          </div>
          {/* Category chips */}
          <div className="px-4 py-3 border-b border-border flex flex-wrap gap-1.5" data-testid="category-chips">
            <button onClick={() => { setActiveCategory("all"); setActiveRank(null); }}
              data-testid="cat-all"
              className={`h-7 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 rounded-full ${
                activeCategory === "all" && !activeRank
                  ? "bg-foreground text-background border-foreground"
                  : "bg-neutral-100 text-black border-neutral-300 hover:bg-neutral-200"
              }`}>All</button>
            {categories.map(c => (
              <button key={c.key} onClick={() => { setActiveCategory(c.key); setActiveRank(null); }}
                data-testid={`cat-${c.key}`}
                className={`h-7 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 rounded-full ${
                  activeCategory === c.key && !activeRank
                    ? "bg-foreground text-background border-foreground"
                    : "bg-neutral-100 text-black border-neutral-300 hover:bg-neutral-200"
                }`}>
                <span className="h-2 w-2 rounded-full" style={{ background: c.color }} /> {c.label}
              </button>
            ))}
          </div>
          {/* Rank chips */}
          <div className="px-4 py-3 border-b border-border" data-testid="rank-chips">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Top 10 by nutrient</div>
            <div className="flex flex-wrap gap-1.5">
              {RANK_NUTRIENTS.map(n => (
                <button key={n} onClick={() => setActiveRank(activeRank === n ? null : n)}
                  data-testid={`rank-${n}`}
                  className={`h-7 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 rounded-full ${
                    activeRank === n
                      ? "bg-[#C8FF3F] text-black border-[#C8FF3F]"
                      : "bg-sky-50 text-sky-900 border-sky-300 hover:bg-sky-100"
                  }`}>
                  {NUTRIENT_LABELS[n]}
                </button>
              ))}
            </div>
          </div>
          {/* Diet filter */}
          <div className="px-4 py-3 border-b border-border flex flex-wrap gap-1.5" data-testid="diet-chips">
            {DIET_FILTERS.map(f => (
              <button key={f.key} onClick={() => setDietFilter(f.key)}
                data-testid={`diet-${f.key}`}
                className={`h-7 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 rounded-full ${
                  dietFilter === f.key
                    ? "bg-accent text-accent-foreground border-accent"
                    : "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                }`}>{f.label}</button>
            ))}
          </div>
          {/* Ingredient grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-border max-h-[500px] overflow-y-auto" data-testid="ingredient-grid">
            {filtered.length === 0 ? (
              <div className="col-span-full p-6 font-mono text-sm text-muted-foreground bg-card">No ingredients match this filter.</div>
            ) : filtered.map(ing => {
              const low = (ing.name || "").toLowerCase();
              const emoji = foodEmoji[low]
                || Object.entries(foodEmoji).find(([k]) => low.includes(k) || k.includes(low))?.[1]
                || "🍽️";
              return (
              <button key={ing.name} onClick={() => addIngredient(ing)}
                data-testid={`ing-${ing.name.replace(/\s+/g, "-")}`}
                className="bg-card p-3 text-left hover:bg-muted transition-colors duration-200 group">
                <div className="aspect-square w-full flex items-center justify-center rounded-xl relative overflow-hidden text-5xl shadow-sm"
                     style={{ background: `linear-gradient(135deg, ${ing.color}55, ${ing.color}22)` }}>
                  <span className="drop-shadow-sm">{emoji}</span>
                </div>
                <div className="mt-2 font-medium capitalize text-sm truncate">{ing.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  {Math.round(ing.nutrients?.calories || 0)} kcal · P{fmt(ing.nutrients?.protein)}
                  {activeRank ? ` · ${NUTRIENT_LABELS[activeRank]}: ${fmt(ing.value ?? ing.nutrients?.[activeRank])}` : ""}
                </div>
              </button>
              );
            })}
          </div>
        </div>

        {/* Selected + Live totals */}
        <div className="space-y-4">
          <div className="border border-border" data-testid="selected-list">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Selected ({selected.length})</div>
              {previewing && <div className="font-mono text-[10px] text-primary flex items-center gap-1"><Sparkle size={10} className="animate-pulse" /> updating</div>}
            </div>
            {selected.length === 0 ? (
              <div className="p-6 font-mono text-sm text-muted-foreground text-center">
                <Plus size={20} className="mx-auto mb-2 text-muted-foreground" />
                Click ingredients on the left to add.
              </div>
            ) : (
              <div className="max-h-[280px] overflow-y-auto">
                {selected.map(r => (
                  <div key={r.rid} className="grid grid-cols-[1fr_80px_60px_28px] gap-2 items-center px-3 py-2 border-b border-border last:border-b-0" data-testid={`selected-${r.name.replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-3 w-3 shrink-0" style={{ background: r.color }} />
                      <span className="capitalize truncate text-sm">{r.name}</span>
                    </div>
                    <input type="number" value={r.quantity} min="0"
                      onChange={e => updateRow(r.rid, "quantity", e.target.value)}
                      data-testid={`selected-qty-${r.name.replace(/\s+/g, "-")}`}
                      className="h-8 px-2 border border-border bg-background font-mono text-right text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
                    <select value={r.unit} onChange={e => updateRow(r.rid, "unit", e.target.value)}
                      className="h-8 px-1 border border-border bg-background font-mono text-xs focus:outline-none focus:border-primary transition-colors duration-200">
                      {["g","ml","oz","cup","tbsp","tsp","piece","slice"].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button onClick={() => removeRow(r.rid)}
                      data-testid={`remove-selected-${r.name.replace(/\s+/g, "-")}`}
                      className="h-8 w-7 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live totals vs DRI — full 44-nutrient dashboard */}
          <div data-testid="live-totals">
            <div className="mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Live nutrients · per serving</div>
              <div className="text-sm font-medium mt-0.5">vs. {profiles.find(p => p.key === profileKey)?.label || profileKey}</div>
            </div>
            <NutrientView
              nutrients={per}
              dri={dri}
              defaultTab="standard"
              defaultView="dashboard"
              testIdPrefix="builder-nutrient-view"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
